import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { SearchableSelect } from "../../components/ui/searchable-select";
import UpdateTag from "../../components/UpdateTag";
import { formatDate } from "../../lib/utils";
import { toast } from "sonner";

const TAG_OPTIONS = [
  { value: "feature", label: "New feature" },
  { value: "improvement", label: "Improvement" },
  { value: "fix", label: "Fix" },
];

export default function Updates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create
  const [form, setForm] = useState({ title: "", body: "", tag: "feature" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/updates").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ title: "", body: "", tag: "feature" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ title: u.title, body: u.body || "", tag: u.tag }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.patch(`/admin/updates/${editing.id}`, form);
      else await api.post("/admin/updates", form);
      setOpen(false);
      load();
      toast.success(editing ? "Update saved" : "Update created (draft)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (u) => {
    try {
      await api.patch(`/admin/updates/${u.id}`, { published: !u.published });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (u) => {
    try {
      await api.delete(`/admin/updates/${u.id}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-updates-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-indigo-600 font-semibold">Product</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">System updates</h1>
          <p className="text-sm text-slate-500 mt-1">Publish what's new — every tenant user sees these in their What's New panel.</p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="update-new-button">New update</Button>
      </div>

      <div className="surface-card divide-y divide-slate-100">
        {items.map((u) => (
          <div key={u.id} className="px-5 py-4 flex items-start justify-between gap-4" data-testid={`update-row-${u.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <UpdateTag tag={u.tag} />
                <span className="font-medium text-slate-800">{u.title}</span>
                {u.published ? (
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-emerald-600">Published {u.published_at ? `· ${formatDate(u.published_at)}` : ""}</span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">Draft</span>
                )}
              </div>
              {u.body && <div className="text-xs text-slate-500 mt-1 line-clamp-2 whitespace-pre-wrap">{u.body}</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0 text-xs">
              <button className="text-slate-500 hover:text-slate-900" onClick={() => togglePublish(u)} data-testid={`update-publish-${u.id}`}>
                {u.published ? "Unpublish" : "Publish"}
              </button>
              <button className="text-slate-500 hover:text-slate-900" onClick={() => openEdit(u)}>Edit</button>
              <button className="text-rose-500 hover:text-rose-700" onClick={() => del(u)}>Delete</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="px-5 py-8 text-center text-xs text-slate-400">No updates yet — create the first one.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit update" : "New update"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="update-form">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Title</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="update-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Tag</Label>
              <SearchableSelect value={form.tag} onValueChange={(v) => setForm({ ...form, tag: v })} options={TAG_OPTIONS} testId="update-tag-select" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Body</Label>
              <textarea
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[110px]"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="What changed, and why users will care…"
                data-testid="update-body-input"
              />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="update-save-button">
                {saving ? "Saving…" : editing ? "Save changes" : "Create draft"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
